'use client';

import React from 'react';
import { Slide, SliderButton, SliderProvider, SliderTrack } from '@faceless-ui/slider';
import { useWindowInfo } from '@faceless-ui/window-info';
import Link from 'next/link';
import type { ExtractLayoutType, SharedBlockType } from '../types';
import type { PagesQuery, ServicesQuery } from '../../graphql/generated/schema';
import { BlockContainer } from '../../components/layouts';
import styles from './styles.module.scss';
import { AppImage, Button } from '../../components';
import queries from '../../styles/queries/index.module.scss';
import colors from '../../styles/colors/index.module.scss';
import { SlideInContainer } from '../../components/animatedContainers';
import { constructClassName } from '../../utils/constructClassName';

export const ServicesScrollerClient: React.FC<SharedBlockType & ExtractLayoutType<PagesQuery, 'ServicesScroller'> & {
  serviceDocs?: ServicesQuery['Services'],
}> = ({ serviceDocs, blockSettings }) => {
  const windowInfo = useWindowInfo();

  const slidesToShow = (() => {
    switch (true) {
      case (windowInfo.width || 0) >= parseInt(queries.lg, 10):
        return 4;
      case (windowInfo.width || 0) >= parseInt(queries.sm, 10):
        return 2;
      case (windowInfo.width || 0) >= parseInt(queries.xxs, 10):
        return 1;
      default:
        return 1;
    }
  })();

  if (!serviceDocs?.docs) return null;

  return (
    <BlockContainer
      className={styles.container}
      blockSettings={blockSettings}
    >
      <div className={styles.sliderTrackOuterContainer}>
        <SliderProvider
          slidesToShow={slidesToShow}
        >
          {({ currentSlideIndex }) => (
            <React.Fragment>
              <SliderTrack className={styles.sliderTrackContainer}>
                {serviceDocs?.docs?.map((service, index) => (
                  <Slide
                    index={index}
                    key={index}
                  >
                    <SlideInContainer
                      delay={index < slidesToShow ? index * 100 : 0}
                    >
                      <Link
                        href={`/services/${service?.slug}`}
                        className={styles.linkContainer}
                      >
                        <div className={styles.cardContainer}>
                          <AppImage
                            imageSize="original"
                            image={service?.featuredImage}
                            className={constructClassName([
                              styles.image,
                              styles.servicesImage,
                            ])}
                          />
                          <h2 className={styles.cardTitle}>{service?.shortTitle}</h2>
                          <p>{service?.excerpt}</p>
                        </div>
                      </Link>
                    </SlideInContainer>
                  </Slide>
                ))}
              </SliderTrack>
              <div className={styles.buttonContainer}>
                <SliderButton
                  className={styles.navButton}
                  direction="prev"
                >
                  <Button
                    type="tertiary"
                    htmlElement="span"
                    icon="circleChevronLeft"
                    iconSize={32}
                    iconFill={colors.grey}
                    disabled={currentSlideIndex === 0}
                  />
                </SliderButton>
                <SliderButton
                  className={styles.navButton}
                  direction="next"
                >
                  <Button
                    type="tertiary"
                    htmlElement="span"
                    icon="circleChevronRight"
                    iconSize={32}
                    iconFill={colors.grey}
                    disabled={currentSlideIndex === (serviceDocs.docs?.length || 0) - slidesToShow}
                  />
                </SliderButton>
              </div>
            </React.Fragment>
          )}
        </SliderProvider>
      </div>
    </BlockContainer>
  );
};
